import {EventEmitter, Injectable} from '@angular/core';
import {ItemService} from './item.service';
import {AuthService} from './auth.service';
import {ContextAccessService} from './context-access.service';
import {NPA_ALERTS} from '../pages/shared/consts/messages';
import {composeMessage} from '../pages/shared/consts/errors';
import {NotificationsService} from '../pages/shared/services/notifications.service';
import {TranslateService} from '../pages/shared/services/translate.service';
import {MatStepper} from '@angular/material/stepper';

@Injectable()
export class NpaFmService {
    components;
    procurementDetails;
    isBidSubmissionMethodElectronic;
    preparationAndAnnouncementService;

    constructor(private authService: AuthService,
                public itemService: ItemService,
                public notificationsService: NotificationsService,
                public translateService: TranslateService,
                public contextAccessService: ContextAccessService) {
    }

    properties(components, procurementDetails, isBidSubmissionMethodElectronic, preparationAndAnnouncementService) {
        this.components = components;
        this.procurementDetails = procurementDetails;
        this.isBidSubmissionMethodElectronic = isBidSubmissionMethodElectronic;
        this.preparationAndAnnouncementService = preparationAndAnnouncementService;
    }

    registerEventMangerLoading(event: EventEmitter<string>) {
        event.subscribe((res: string) => {
            if (res === 'upload-submit-started') {
                this.notificationsService.startLoading();
            } else if (res === 'upload-submit-completed') {
                this.notificationsService.dismissLoading();
            }
        });
    }

    handleError(error) {
        const errorString = error && error.error && error.error.error;
        if (errorString && typeof errorString === 'string') {
            const message = errorString.split('%');
            if (message.length > 0) {
                const messageDetail = message[1] && message[1].split('@');
                if (messageDetail.length > 0) {
                    if (messageDetail[0] === 'maxSize') {
                        setTimeout(() => {
                            this.notificationsService
                                .error(NPA_ALERTS[this.translateService.lang].FILE_SIZE + (+messageDetail[1] / 1024).toFixed(1) + 'MB' + NPA_ALERTS[this.translateService.lang].BE);
                        }, 500);
                    } else if (messageDetail[0] === 'allowedExtensions') {
                        setTimeout(() => {
                            this.notificationsService
                                .error(NPA_ALERTS[this.translateService.lang].INVALID_FORMAT + `( ${messageDetail[1]} )`);
                        }, 500);
                    } else if (messageDetail[0] === 'needsLoginToDownload') {
                        setTimeout(() => {
                            this.notificationsService.error(NPA_ALERTS[this.translateService.lang].NEED_FOR_LOGIN);
                        }, 500);
                    }
                }
            }
        }
    }

    ifFilesValidNextStep(config, stepper?: MatStepper) {
        this.notificationsService.startLoading();
        config.isValid().then((response: any) => {
            if (response && response.upload) {
                this.notificationsService.success(NPA_ALERTS[this.translateService.lang].ADD_SUCCESS);
                if (stepper) {
                    stepper.next();
                }
                return true;
            }
            this.notificationsService.error(response.relatedFile['name_' + this.translateService.lang] + NPA_ALERTS[this.translateService.lang].NOT_UPLOADED);
            return false;
        }, (error) => {
            this.notificationsService.error(composeMessage(error));
            return false;
        });
    }

    isFilesValid(config): Promise<boolean> {
        this.notificationsService.startLoading();
        return new Promise((resolve) => {
            return resolve(true);
            config.isValid().then((response: any) => {
                if (response && response.upload) {
                    this.notificationsService.success(NPA_ALERTS[this.translateService.lang].ADD_SUCCESS);
                    return resolve(true);
                }
                this.notificationsService.error(response.relatedFile['name_' + this.translateService.lang] + NPA_ALERTS[this.translateService.lang].NOT_UPLOADED);
                return resolve(false);
            }, (error) => {
                this.notificationsService.error(composeMessage(error));
                return resolve(false);
            });
        });
    }

    isFilesValidWithOutMessage(config): Promise<boolean> {
        return new Promise((resolve) => {
            config.isValid().then((response: any) => {
                if (response && response.upload) {
                    return resolve(true);
                }
                this.notificationsService.error(response.relatedFile['name_' + this.translateService.lang] + NPA_ALERTS[this.translateService.lang].NOT_UPLOADED);
                return resolve(false);
            }, (error) => {
                this.notificationsService.error(composeMessage(error));
                return resolve(false);
            });
        });
    }

    accesses(operations: string[]): INpaFMComponent {
        return {
            assignment: this.contextAccessService.can(
                'aets-list-of-procurement-opportunities',
                'context_operation', operations,
                {
                    processing_body: this.itemService.info.getValue().data.procurement_processing_body_slug,
                    awarding_body: this.itemService.info.getValue().data.awarding_body_slug,
                    procurement_entity_slug: this.itemService.info.getValue().data.procurement_entity_slug,
                    tender_revision_id: this.itemService.info.getValue().data.tender_revision_id
                }),
            initiation: this.contextAccessService.can(
                'aets-beginning-of-procurement-process',
                'context_operation',
                operations,
                null,
                (this.authService.loggedInUser || {} as any).sub_party_slug === this.itemService.info.getValue().data.procurement_entity_slug
                || (this.authService.loggedInUser || {} as any).sub_party_slug === 'national-procurement-authority') &&
                (operations.includes('download') ? true : (['new', 'modified'].includes((this.components.initiation || {} as any).current_flow_status) || !this.components.initiation))
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            preparation: this.contextAccessService.can(
                'aets-bidding-and-procurement-notice',
                'context_operation',
                operations)
                && !((this.components.preparation || {} as any).current_flow_status === 'submitted'
                    || (this.components.preparation || {} as any).current_flow_status === 'approved')
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            preparationAmendment: this.contextAccessService.can('aets-bidding-amendment', 'context_operation',
                operations)
                && !((this.components.preparation_amendment || {} as any).current_flow_status === 'submitted'
                || (this.components.preparation_amendment || {} as any).current_flow_status === 'approved')
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            clarification: this.contextAccessService.can('aets-description-request', 'context_operation', operations),
            clarificationGeneral: this.contextAccessService.can('aets-public-remarks', 'context_operation', operations),
            preBidMeeting: this.contextAccessService.can('aets-pre-bid-meeting', 'context_operation', operations),
            siteVisit: this.contextAccessService.can('aets-area-visit', 'context_operation', operations),
            bidSubmission: this.contextAccessService.can(
                'aets-bid-arrangement-and-submit',
                'context_operation',
                operations)
                && !((this.components.bidSubmission || {} as any).current_flow_status === 'submitted'
                    || (this.components.bidSubmission || {} as any).current_flow_status === 'approved')
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.isBidSubmissionMethodElectronic,
            bidRenewal: this.contextAccessService.can('aets-bid-arrangement-and-submit', 'context_operation', operations) &&
                (this.components.bid_submission || {} as any).current_flow_status === 'submitted' &&
                !(this.components.bid_opening || {} as any).current_flow_status &&
                this.components.bid_submission_date_time && this.isBidSubmissionMethodElectronic &&
                this.procurementDetails.tender_revision_status_slug !== 'cancelled',
            bidWithdrawal: this.contextAccessService.can('aets-bid-arrangement-and-submit', 'context_operation', operations) &&
                (this.components.bid_submission || {} as any).current_flow_status === 'submitted' && !(this.components.bid_opening || {} as any).current_flow_status &&
                this.components.bid_submission_date_time && this.isBidSubmissionMethodElectronic && this.procurementDetails.tender_revision_status_slug !== 'cancelled',
            bidSubmissionAlternate: this.contextAccessService.can('aets-bid-arrangement-and-submit', 'context_operation', operations) &&
                (this.components.bid_submission || {} as any).current_flow_status === 'submitted' && !(this.components.bid_opening || {} as any).current_flow_status
                && this.components.bid_submission_date_time && this.isBidSubmissionMethodElectronic
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled' &&
                ((this.components.preparation || {} as any).is_alternative_bids_applicable || (this.components.preparation_amendment || {} as any).is_alternative_bids_applicable)
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled',
            bidValidityExtension: this.contextAccessService.can('aets-extend-bid-validity', 'context_operation',
                operations) &&
                (this.components.bid_submission || {} as any).current_flow_status === 'submitted' && !(this.components.bid_opening || {} as any).current_flow_status
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            bidOpening:
                this.contextAccessService.can(
                    'bidding',
                    'context_operation',
                    operations)
                && !((this.components.bid_opening || {} as any).current_flow_status === 'submitted'
                || (this.components.bid_opening || {} as any).current_flow_status === 'closed')
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            bidEvaluation:
                this.contextAccessService.can('aets-bid-evaluation', 'context_operation',
                    operations)
                && (this.components.bid_evaluation || {} as any).current_flow_status !== 'submitted'
                && (this.components.bid_evaluation || {} as any).current_flow_status !== 'approved',
            bidEvaluationExtension:
                this.contextAccessService.can('aets-extended-evaluation-period', 'context_operation',
                    operations,
                    null, true)
                && (this.components.bid_evaluation_period_extension || {} as any).current_flow_status !== 'submitted'
                && (this.components.bid_evaluation_period_extension || {} as any).current_flow_status !== 'approved'
                && (this.components.bid_opening || {} as any).current_flow_status === 'closed'
                && !(this.components.contract_award_decision_announcement || {} as any).current_flow_status
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            contractNegotiation:
                this.contextAccessService.can('aets-contract-negotiations', 'context_operation',
                    operations,
                    null, true)
                && (this.components.bid_evaluation || {} as any).current_flow_status === 'approved'
                && (this.components.contract_negotiation || {} as any).current_flow_status !== 'submitted'
                && (this.components.contract_negotiation || {} as any).current_flow_status !== 'approved',
            contractAwardDecision:
                this.contextAccessService.can('aets-contract-award-notice', 'context_operation',
                    operations)
                && (this.components.contract_award_decision_announcement || {} as any).current_flow_status !== 'submitted'
                && (this.components.contract_award_decision_announcement || {} as any).current_flow_status !== 'approved'
                && (this.components.complaint || {} as any).current_flow_status !== 'submitted'
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            complaint: this.contextAccessService.can('aets-complaint', 'context_operation',
                operations)
                && (this.components.contract_award_decision_announcement || {} as any).current_flow_status === 'approved'
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.isBidSubmissionMethodElectronic,
            contract: this.contextAccessService.can('aets-contract', 'context_operation', operations, null, true)
                && (this.components.contract_signing || {} as any).current_flow_status !== 'submitted'
                && (this.components.contract_signing || {} as any).current_flow_status !== 'approved'
                && (this.components.contract_award_decision_announcement || {} as any).current_flow_status === 'approved'
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            cancellation:
                this.contextAccessService.can(
                    'aets-termination-of-procurement-process',
                    'context_operation',
                    operations)
                && this.isBidSubmissionMethodElectronic,
            renewal: this.contextAccessService.can(
                'aets-resumption-of-the-procurement-process',
                'context_operation',
                operations)
                && (this.components.renewal || {} as any).current_flow_status !== 'submitted'
                && (this.components.renewal || {} as any).current_flow_status !== 'approved'
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.procurementDetails.tender_revision_status_slug !== 'unresolved-tender'
                && this.isBidSubmissionMethodElectronic,
            miscellaneousReport: this.contextAccessService.can('aets-miscellaneous-procurement-process-report', 'context_operation',
                ['add', 'edit', 'submit', 'approve', 'reject', 'upload', 'delete'])
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.isBidSubmissionMethodElectronic,
            NPCMiscellaneousRequests:
                this.contextAccessService.can('aets-miscellaneous-requests-of-commission', 'context_operation',
                    ['add', 'edit', 'submit', 'approve', 'reject', 'upload', 'delete'])
                && this.procurementDetails.tender_revision_status_slug !== 'cancelled'
                && this.isBidSubmissionMethodElectronic
        };

    }
}

interface INpaFMComponent {
    assignment: boolean;
    initiation: boolean;
    preparation: boolean;
    preparationAmendment: boolean;
    clarification: boolean;
    clarificationGeneral: boolean;
    NPCMiscellaneousRequests: boolean;
    miscellaneousReport: boolean;
    renewal: boolean;
    cancellation: boolean;
    contract: boolean;
    complaint: boolean;
    contractAwardDecision: boolean;
    contractNegotiation: boolean;
    bidEvaluationExtension: boolean;
    bidEvaluation: boolean;
    bidOpening: boolean;
    bidValidityExtension: boolean;
    bidSubmissionAlternate: boolean;
    bidWithdrawal: boolean;
    bidRenewal: boolean;
    bidSubmission: boolean;
    siteVisit: boolean;
    preBidMeeting: boolean;
}
